import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import { Button, Modal, Badge, Row, Col, Spinner, Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faEye, faCog, faList, faArrowRight, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from 'axios';
import { Event, WarehouseTask } from '../interfaces';
import { API_BASE_URL, buildApiUrl } from '../config/api';
import DefectReportGeneralModal from './DefectReportGeneralModal';

moment.locale('he');

const localizer = momentLocalizer(moment);

interface EventCalendarProps {
    onEventClick: (eventId: number) => void;
    onCreateNewEvent: (startDate: Date, endDate: Date) => void;
    onGoToEventList: () => void;
}

const EventCalendar: React.FC<EventCalendarProps> = ({ onEventClick, onCreateNewEvent, onGoToEventList }) => {
  const { user } = useAuth();
  const isTechnician = user?.role?.name === 'TECHNICIAN';
  const [events, setEvents] = useState<Event[]>([]);
  const [warehouseTasks, setWarehouseTasks] = useState<WarehouseTask[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<WarehouseTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDefectReportModal, setShowDefectReportModal] = useState<boolean>(false);

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentView, setCurrentView] = useState<View>('month');
  const [jumpToDate, setJumpToDate] = useState<string>(moment().format('YYYY-MM-DD'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsResponse, tasksResponse] = await Promise.all([
          axios.get(buildApiUrl('events/events/')),
          axios.get(buildApiUrl('events/warehouse-events/'))
        ]);
        
        setEvents(eventsResponse.data);
        setWarehouseTasks(tasksResponse.data);
        setLoading(false);
      } catch (err) {
        console.error("שגיאה בטעינת נתונים ללוח שנה:", err);
        setError("לא ניתן לטעון נתונים ללוח השנה. וודא ששרת Django פועל.");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isTechnician) {
      setShowDefectReportModal(false);
    }
  }, [isTechnician]);

  const calendarEvents = useMemo(() => {
    const eventItems = events.map(event => ({
      id: event.id,
      title: event.event_name,
      start: new Date(event.start_date),
      end: new Date(event.end_date),
      resource: event,
      allDay: false,
      type: 'event'
    }));

    const taskItems = warehouseTasks.map(task => ({
      id: `task-${task.id}`,
      title: task.event_name,
      start: new Date(task.start_date),
      end: new Date(task.end_date),
      resource: task,
      allDay: false,
      type: 'warehouse_task'
    }));

    return [...eventItems, ...taskItems];
  }, [events, warehouseTasks]);

  const onNavigate = (newDate: Date, view: View) => {
    setCurrentDate(newDate);
    setCurrentView(view);
  };

   const onView = (newView: View) => {
    setCurrentView(newView);
  };

  const handleJumpToDate = () => {
    if (jumpToDate) {
      setCurrentDate(new Date(jumpToDate));
    }
  };
  
  const handleSelectEvent = (event: any) => {
    if (event.type === 'warehouse_task') {
      // משימת מחסן - הצג מודל עם פרטים
      setSelectedTask(event.resource);
      setShowTaskModal(true);
    } else if (event.id) {
      // אירוע רגיל
      onEventClick(event.id);
    }
  };

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    if (user?.role?.name === 'TECHNICIAN' || user?.role?.name === 'TEAM_LEADER') {
      return; // טכנאי לא פותח אירוע חדש בלחיצה על תאריך ריק
    }
    onCreateNewEvent(start, end);
  };

  const handleDefectReport = async (_defectData: any) => {
    /* הודעת הצלחה מוצגת ב-DefectReportGeneralModal */
  };

  const eventPropGetter = useMemo(() => {
    return (event: any) => {
      // בדיקה אם זה משימת מחסן
      if (event.type === 'warehouse_task') {
        return { style: { backgroundColor: '#8A2BE2', color: '#fff' } };
      }

      // אירוע רגיל
      const eventStatus = event.resource.status;
      let backgroundColor = '#3174ad'; // צבע ברירת מחדל
      let color = '#fff';

      switch (eventStatus) {
        case 'planned':
          backgroundColor = '#a2d2ff'; // תכלת בהיר
          color = '#000';
          break;
        case 'preparation':
          backgroundColor = '#ffc107'; // צהוב (אזהרה)
          color = '#000';
          break;
        case 'active':
          backgroundColor = '#28a745'; // ירוק (הצלחה)
          break;
        case 'completed':
          backgroundColor = '#6c757d'; // אפור (מושלם)
          break;
        case 'cancelled':
          backgroundColor = '#dc3545'; // אדום (סכנה)
          break;
        case 'no_equipment_list':
          backgroundColor = '#f8bbd0'; // ורוד בהיר ל"ללא רשימת ציוד"
          color = '#000';
          break;
        default:
          backgroundColor = '#3174ad';
      }

      return { style: { backgroundColor, color } };
    };
  }, []);

  const getTaskTypeLabel = (type: string) => {
    switch (type) {
      case 'inventory_check':
        return 'בדיקת מלאי';
      case 'stock_taking':
        return 'ציפורת מלאי';
      case 'maintenance':
        return 'תחזוקה';
      case 'cleaning':
        return 'ניקיון';
      default:
        return type;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'planned':
        return 'מתוכנן';
      case 'preparation':
        return 'בהכנה';
      case 'active':
        return 'פעיל/בחוץ';
      case 'completed':
        return 'הסתיים';
      case 'cancelled':
        return 'בוטל';
      case 'no_equipment_list':
        return 'ללא רשימת ציוד';
      default:
        return status;
    }
  };

  const getRecurrenceLabel = (pattern: string) => {
    switch (pattern) {
      case 'daily':
        return 'יומי';
      case 'weekly':
        return 'שבועי';
      case 'monthly':
        return 'חודשי';
      case 'yearly':
        return 'שנתי';
      default:
        return pattern;
    }
  };


  if (loading) return <Spinner animation="border" role="status"><span className="visually-hidden">טוען לוח שנה...</span></Spinner>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>לוח שנה לאירועים</h2>
        <div>
          <Button
            variant="warning"
            onClick={() => setShowDefectReportModal(true)}
            className="me-2"
            disabled={isTechnician}
            title={isTechnician ? 'אין לטכנאי הרשאה לדווח תקלות; רק ראש צוות ומעלה' : ''}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            דיווח על ציוד תקול
          </Button>
          <Button variant="info" onClick={onGoToEventList}>
            <FontAwesomeIcon icon={faList} className="me-1" />
            רשימת אירועים
          </Button>
        </div>
      </div>
      {/* מקרא צבעים */}
      <div className="mb-3 p-2 border rounded bg-light">
        <Row className="g-3">
          <Col md={2} sm={4} xs={6}>
            <div className="d-flex align-items-center">
              <span style={{ width: 18, height: 18, backgroundColor: '#a2d2ff', border: '1px solid #ccc', display: 'inline-block', marginLeft: 8 }}></span>
              <span>מתוכנן</span>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div className="d-flex align-items-center">
              <span style={{ width: 18, height: 18, backgroundColor: '#ffc107', border: '1px solid #ccc', display: 'inline-block', marginLeft: 8 }}></span>
              <span>בהכנה</span>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div className="d-flex align-items-center">
              <span style={{ width: 18, height: 18, backgroundColor: '#28a745', border: '1px solid #ccc', display: 'inline-block', marginLeft: 8 }}></span>
              <span>פעיל/בחוץ</span>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div className="d-flex align-items-center">
              <span style={{ width: 18, height: 18, backgroundColor: '#6c757d', border: '1px solid #ccc', display: 'inline-block', marginLeft: 8 }}></span>
              <span>הסתיים</span>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div className="d-flex align-items-center">
              <span style={{ width: 18, height: 18, backgroundColor: '#dc3545', border: '1px solid #ccc', display: 'inline-block', marginLeft: 8 }}></span>
              <span>בוטל</span>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div className="d-flex align-items-center">
              <span style={{ width: 18, height: 18, backgroundColor: '#f8bbd0', border: '1px solid #ccc', display: 'inline-block', marginLeft: 8 }}></span>
              <span>ללא רשימת ציוד</span>
            </div>
          </Col>
        </Row>
      </div>
      <Form className="mb-4 p-3 border rounded bg-light">
        <Row className="align-items-end">
          <Col md={4}>
            <Form.Group controlId="jumpToDate">
              <Form.Label>קפוץ לתאריך ספציפי</Form.Label>
              <Form.Control
                type="date"
                value={jumpToDate}
                onChange={(e) => setJumpToDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Button onClick={handleJumpToDate} className="w-100">
              <FontAwesomeIcon icon={faArrowRight} className="me-1" />
              קפוץ
            </Button>
          </Col>
        </Row>
      </Form>


      <div style={{ height: '700px', margin: '20px 0' }}>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          
          style={{ height: '100%' }}
          rtl
          views={['month', 'week', 'day', 'agenda']}
          defaultView="month"
          view={currentView}
          date={currentDate}
          onNavigate={onNavigate}
          onView={onView}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          eventPropGetter={eventPropGetter}
          messages={{
            next: 'הבא',
            previous: 'הקודם',
            today: 'היום',
            month: 'חודש',
            week: 'שבוע',
            day: 'יום',
            agenda: 'סדר יום',
            date: 'תאריך',
            time: 'שעה',
            event: 'אירוע',
            noEventsInRange: 'אין אירועים בתאריכים שנבחרו.',
            showMore: total => `+עוד ${total}`,
          }}
        />
      </div>

      {/* מודל להצגת פרטי משימת מחסן */}
      <Modal show={showTaskModal} onHide={() => setShowTaskModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>פרטי משימת מחסן</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <div>
              <Row>
                <Col md={6}>
                  <h5>{selectedTask.event_name}</h5>
                  <p><strong>סוג:</strong> {getTaskTypeLabel(selectedTask.task_type)}</p>
                  <p><strong>סטטוס:</strong> {getStatusLabel(selectedTask.status)}</p>
                  <p><strong>תאריך התחלה:</strong> {new Date(selectedTask.start_date).toLocaleString('he-IL')}</p>
                  <p><strong>תאריך סיום:</strong> {new Date(selectedTask.end_date).toLocaleString('he-IL')}</p>
                </Col>
                <Col md={6}>
                  <p><strong>מחזוריות:</strong> {selectedTask.is_recurring ? 'כן' : 'לא'}</p>
                  {selectedTask.is_recurring && (
                    <>
                      <p><strong>דפוס:</strong> {getRecurrenceLabel(selectedTask.recurrence_pattern)}</p>
                      {selectedTask.recurrence_end_date && (
                        <p><strong>עד תאריך:</strong> {new Date(selectedTask.recurrence_end_date).toLocaleDateString('he-IL')}</p>
                      )}
                    </>
                  )}
                  {selectedTask.color && (
                    <p>
                      <strong>צבע:</strong> 
                      <span
                        style={{
                          display: 'inline-block',
                          width: '20px',
                          height: '20px',
                          backgroundColor: selectedTask.color.hex_code || '#8A2BE2',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          marginRight: '5px'
                        }}
                      ></span>
                      {selectedTask.color.name || 'משימת מחסן'}
                    </p>
                  )}
                </Col>
              </Row>
              
              {selectedTask.description && (
                <div className="mt-3">
                  <h6>תיאור:</h6>
                  <p>{selectedTask.description}</p>
                </div>
              )}
              
              {selectedTask.notes && (
                <div className="mt-3">
                  <h6>הערות:</h6>
                  <p>{selectedTask.notes}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTaskModal(false)}>
            סגור
          </Button>
        </Modal.Footer>
      </Modal>

      <DefectReportGeneralModal
        show={showDefectReportModal && !isTechnician}
        onHide={() => setShowDefectReportModal(false)}
        onDefectReport={handleDefectReport}
      />
    </div>
  );
};

export default EventCalendar;
